import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { Avatar, Card, Button, EmptyState, Icon } from '@/components/atoms';
import type { IconName } from '@/components/atoms';
import { CompletenessWidget, PresenceSwitcher } from '@/components/domain';
import { useAuthStore } from '@/store';
import { auth as authApi, specialists as spApi } from '@/api';
import { logger } from '@/utils/logger';
import { toast } from '@/components/atoms/Toast';
import type { SpecialistProfile } from '@/types/domain';
import styles from './pages.module.css';

interface Row {
  icon: IconName;
  label: string;
  to: string;
}

interface RowGroup {
  title: string;
  rows: Row[];
}

function MenuRow({ icon, label }: { icon: IconName; label: string }): JSX.Element {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <Icon name={icon} size={20} />
      <span>{label}</span>
    </span>
  );
}

export function MyProfilePage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  // F-180 C12: surface destructive Logout outside /settings.
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const isSpecialist = user?.role === 'specialist';
  const [profile, setProfile] = useState<SpecialistProfile | null>(null);

  const handleLogout = (): void => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    if (!isSpecialist) return;
    spApi
      .updateMySpecialist({})
      .then((p) => setProfile(p))
      .catch((err) => logger.warn('me-detail load failed', err));
  }, [isSpecialist]);

  if (!user) {
    return (
      <Layout title="Я">
        <EmptyState
          icon="cat"
          message="Відкрий Mini App через Telegram — і профіль засвітиться."
        />
      </Layout>
    );
  }

  const becomeSpecialist = async (): Promise<void> => {
    try {
      const updated = await authApi.becomeSpecialist();
      setUser(updated);
      toast.success('Заявку на специаліста надіслано');
    } catch (err) {
      logger.error('Become specialist failed', err);
      toast.error('Не вдалось створити профіль');
    }
  };

  // Cabinet grouping (F-160 A6, NN/g H8). A flat 13-item list overwhelmed
  // specialists; chunk into four semantic sections so the eye can scan.
  // Order inside groups mirrors the spec verbatim.
  const specialistGroups: RowGroup[] = [
    {
      title: 'Контент',
      rows: [
        { icon: 'image', label: 'Портфоліо', to: '/me/portfolio' },
        { icon: 'info', label: 'Q&A', to: '/me/qa' },
        { icon: 'search', label: 'Розділи', to: '/me/categories' },
        { icon: 'home', label: 'Міста', to: '/me/cities' },
        { icon: 'presets', label: 'Пресети послуг', to: '/me/presets' },
        // F-180 (B4): product authoring lives under "Контент" between
        // presets (services) and Q&A so the specialist sees both
        // monetisation entry-points side by side.
        { icon: 'package', label: 'Товари', to: '/me/products' },
      ],
    },
    {
      title: 'Графік',
      rows: [
        { icon: 'calendar', label: 'Календар', to: '/me/calendar' },
        { icon: 'calendar', label: 'Робочі години', to: '/me/working-hours' },
        // F-170 B3.5: specialist authors a Maplibre polygon for their
        // coverage zone; clients see it read-only in the profile feed.
        { icon: 'map-pin', label: 'Зона обслуговування', to: '/me/service-area' },
      ],
    },
    {
      title: 'Адмін',
      rows: [
        { icon: 'stats', label: 'Статистика', to: '/me/stats' },
        { icon: 'crm', label: 'CRM', to: '/me/crm' },
        { icon: 'verify', label: 'Верифікація', to: '/me/verification' },
        { icon: 'favorite', label: 'Закладки', to: '/favorites' },
        { icon: 'saved', label: 'Збережені пошуки', to: '/search/saved' },
      ],
    },
    {
      title: 'Налаштування',
      rows: [
        { icon: 'settings', label: 'Налаштування', to: '/settings' },
        { icon: 'blocked', label: 'Заблоковані клієнти', to: '/me/blocked' },
      ],
    },
  ];

  return (
    <Layout title="Я">
      <Card>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Avatar
            name={user.first_name}
            size={64}
            presence={user.specialist_profile?.presence_status}
          />
          <div>
            <strong style={{ fontSize: 18 }}>{user.first_name}</strong>
            <div className={styles.note}>
              {isSpecialist ? 'Спеціаліст' : 'Клієнт'}
              {user.tg_username ? ` · @${user.tg_username}` : null}
            </div>
          </div>
        </div>
      </Card>

      {isSpecialist && profile ? (
        <>
          <div style={{ marginTop: 12 }}>
            <PresenceSwitcher
              initial={{
                presence_status: profile.presence_status,
                presence_message: profile.presence_message ?? '',
              }}
              onChange={setProfile}
            />
          </div>
          <div style={{ marginTop: 12 }}>
            <CompletenessWidget user={user} profile={profile} />
          </div>
        </>
      ) : null}

      <div className={styles.list}>
        <Card interactive onClick={() => navigate('/me/edit')}>
          <MenuRow icon="edit" label="Редагувати профіль" />
        </Card>
        {isSpecialist ? (
          specialistGroups.map((group) => (
            <section
              key={group.title}
              className={styles.cabinetGroup}
              aria-label={group.title}
            >
              <h3 className={styles.cabinetGroupTitle}>{group.title}</h3>
              <div className={styles.list}>
                {group.rows.map((r) => (
                  <Card key={r.to} interactive onClick={() => navigate(r.to)}>
                    <MenuRow icon={r.icon} label={r.label} />
                  </Card>
                ))}
              </div>
            </section>
          ))
        ) : (
          <>
            <Card>
              <strong>Стати спеціалістом</strong>
              <p className={styles.note}>0% комісії · модерація ≤24 год.</p>
              <Button onClick={becomeSpecialist} style={{ marginTop: 12 }}>
                Заявка
              </Button>
            </Card>
            {/* F-170 B3.6: client-side month calendar with dots on days
                that host an upcoming order. */}
            <Card interactive onClick={() => navigate('/calendar')}>
              <MenuRow icon="calendar" label="Графік" />
            </Card>
            <Card interactive onClick={() => navigate('/favorites')}>
              <MenuRow icon="favorite" label="Закладки" />
            </Card>
            <Card interactive onClick={() => navigate('/search/saved')}>
              <MenuRow icon="saved" label="Збережені пошуки" />
            </Card>
            <Card interactive onClick={() => navigate('/settings')}>
              <MenuRow icon="settings" label="Налаштування" />
            </Card>
          </>
        )}
        {/* F-180 C12: destructive Logout row at the cabinet bottom so
            users don't have to dig through /settings to leave the
            session. */}
        <div style={{ marginTop: 12 }}>
          <Button variant="destructive" fullWidth onClick={handleLogout}>
            Вийти
          </Button>
        </div>
      </div>
    </Layout>
  );
}
